// Action Center presentation helpers — pure, shared by page and tests.

export function greetingForHour(hour, name) {
  const daypart = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening";
  return `Good ${daypart}${name ? `, ${name}` : ""}.`;
}

export function priorityGlyph(score) {
  if (score >= 60) return "🔴";
  if (score >= 40) return "🟡";
  if (score >= 25) return "🟢";
  return "🔵";
}

export function formatEstimate(seconds) {
  if (!seconds) return "";
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

// Continuation-aware decision states — human labels, never raw enums.
// Human decision state (action) and workflow execution state (continuation)
// are distinct; the UI shows the combination honestly.
export const CONTINUATION_STATE_LABELS = {
  waiting: "Waiting for your decision",
  applying: "Applying your decision…",
  verification_pending: "Verifying the result…",
  succeeded: "Completed",
  failed_recoverable: "Needs follow-up — safe to retry",
  reconciliation_required: "Reconciliation required",
  failed_terminal: "Failed safely — resolved via follow-up",
  superseded: "Superseded by a newer version",
  replay: "Already resolved",
  expired: "Link expired",
  stale: "Changed since this was issued",
  completed_elsewhere: "Already resolved",
};

export const DECISION_LABELS = {
  approve: "Approve",
  reject: "Reject",
  request_changes: "Request changes",
  resolve_queue: "Confirm queue resolved",
  reconcile: "Record reconciliation",
  reverify: "Re-verify mapping",
  reject_mapping: "Reject mapping",
  reconnected: "I reconnected — verify now",
  acknowledge_degradation: "Acknowledge degradation",
  dismiss: "Dismiss",
};

export function continuationStateLabel(state) {
  return CONTINUATION_STATE_LABELS[state] || String(state || "").replace(/_/g, " ");
}

export function decisionLabel(decision) {
  return DECISION_LABELS[decision] || String(decision || "").replace(/_/g, " ");
}

// Interpret a /decide response into a user-facing outcome. Replays and
// races are informative resolutions, never generic failures.
export function interpretDecideResponse(payload) {
  // Pre-claim conflicts (state/generation moved, action terminal) arrive as
  // {error, code} with NO continuation — they are refusals, never progress.
  if (payload && !payload.replay && !payload.continuationState && (payload.code || payload.error)) {
    const terminal = payload.code === "continuation_action_terminal";
    return {
      tone: "warning",
      label: terminal
        ? CONTINUATION_STATE_LABELS.completed_elsewhere
        : CONTINUATION_STATE_LABELS.stale,
      detail: payload.error || "The decision changed since your view was loaded.",
      refresh: true,
    };
  }
  if (payload?.replay) {
    return {
      tone: "info",
      label: continuationStateLabel("replay"),
      detail: payload.explanation || "This decision was already recorded.",
      refresh: true,
    };
  }
  const state = payload?.continuationState;
  if (state === "succeeded") {
    return {
      tone: "success",
      label: continuationStateLabel("succeeded"),
      detail:
        payload?.warning ||
        "Your decision was applied and the workflow verified its result.",
      refresh: true,
    };
  }
  if (state === "reconciliation_required") {
    return {
      tone: "warning",
      label: continuationStateLabel(state),
      detail:
        "Your decision was applied, but the result could not be verified. A follow-up task tracks exactly what is known — nothing will be retried blindly.",
      refresh: true,
    };
  }
  if (state === "failed_recoverable") {
    return {
      tone: "warning",
      label: continuationStateLabel(state),
      detail:
        payload?.error ||
        "The decision could not be applied; nothing irreversible happened. It is safe to try again.",
      refresh: true,
    };
  }
  return {
    tone: "info",
    label: continuationStateLabel(state || "applying"),
    detail: payload?.error || payload?.explanation || "",
    refresh: true,
  };
}
